import type { ExtendedSession } from './index.types';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { EventService } from 'Modules/softphone/services/EventService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { webrtcLogger as logger } from 'Modules/softphone/utils/logger';
import { Session } from 'sip.js';
export type {
  ExtendedSessionDescriptionHandler,
  ExtendedSession,
} from './index.types';

/**
 * Manages RTCPeerConnection instances and media streams for active calls.
 * Extracts audio tracks, handles microphone switching, and caches remote streams.
 */
export class WebRTCService {
  private static instance: WebRTCService;
  private peerConnections: Map<string, RTCPeerConnection> = new Map();
  private remoteStreams: Map<string, MediaStream> = new Map();

  private constructor() {}

  static getInstance(): WebRTCService {
    if (!WebRTCService.instance) {
      WebRTCService.instance = new WebRTCService();
    }
    return WebRTCService.instance;
  }

  /**
   * Setup peer connection from SIP session
   * Extracts the RTCPeerConnection from session description handler
   */
  setupPeerConnection(
    session: Session,
    callId: string
  ): RTCPeerConnection | null {
    // Skip if already setup for this call
    const existingPc = this.peerConnections.get(callId);
    if (existingPc) {
      logger.debug(`Peer connection already setup for call: ${callId}`);
      return existingPc;
    }

    logger.debug(`Setting up peer connection for call: ${callId}`);

    const extendedSession = session as ExtendedSession;

    const pc = extendedSession.sessionDescriptionHandler?.peerConnection;

    if (!pc) {
      logger.warn('No peer connection found in session (may not be ready yet)');
      return null;
    }

    logger.debug(
      `Found peer connection - state: ${pc.connectionState}, ICE: ${pc.iceConnectionState}`
    );

    this.peerConnections.set(callId, pc);

    // Setup event handlers
    pc.onconnectionstatechange = () => {
      logger.debug(`Connection state changed to: ${pc.connectionState}`);
      if (pc.connectionState === 'connected') {
        logger.info('WebRTC connection established!');
        EventService.getInstance().logRtpEvent('incoming', 'RTP stream started', {
          callId,
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
        });
        // Update remote stream when connection is established
        const remoteStream = this.getRemoteStream(callId);

        if (remoteStream && remoteStream.getTracks().length > 0) {
          logger.info('Auto-updating remote stream on connection established', {
            callId,
            trackCount: remoteStream.getTracks().length
          });
          const audioService = AudioService.getInstance();
          audioService.setRemoteStream(remoteStream);
        }
      } else if (
        pc.connectionState === 'failed' ||
        pc.connectionState === 'closed'
      ) {
        logger.warn('WebRTC connection failed/closed');
        EventService.getInstance().logRtpEvent('incoming', `RTP stream ${pc.connectionState}`, {
          callId,
          connectionState: pc.connectionState,
          iceConnectionState: pc.iceConnectionState,
        });
      }
    };

    pc.oniceconnectionstatechange = () => {
      logger.debug(`ICE connection state changed to: ${pc.iceConnectionState}`);
      if (pc.iceConnectionState === 'connected' || pc.iceConnectionState === 'completed') {
        EventService.getInstance().logRtpEvent('incoming', `ICE ${pc.iceConnectionState}`, {
          callId,
          iceConnectionState: pc.iceConnectionState,
        });
      }
    };

    pc.ontrack = (event) => {
      logger.info(
        `Track received: ${event.track.kind}, ${event.streams.length} streams, enabled: ${String(event.track.enabled)}, readyState: ${event.track.readyState}`
      );

      EventService.getInstance().logRtpEvent('incoming', `${event.track.kind} track received`, {
        callId,
        trackKind: event.track.kind,
        trackEnabled: event.track.enabled,
        trackReadyState: event.track.readyState,
        streamCount: event.streams.length,
      });

      // Automatically update remote stream when track is received
      if (event.track.kind === 'audio') {
        logger.info('Audio track received, updating remote stream', { callId });
        const remoteStream = this.getRemoteStream(callId);
        if (remoteStream && remoteStream.getTracks().length > 0) {
          const audioService = AudioService.getInstance();
          audioService.setRemoteStream(remoteStream);
        }
      }
    };

    return pc;
  }

  /**
   * Get local media stream from peer connection
   */
  getLocalStream(callId: string): MediaStream | null {
    const pc = this.peerConnections.get(callId);
    if (!pc) return null;

    const localStream = new MediaStream();
    pc.getSenders().forEach((sender) => {
      if (sender.track && sender.track.kind === 'audio') {
        localStream.addTrack(sender.track);
      }
    });

    return localStream;
  }

  /**
   * Get remote media stream from peer connection
   * IMPORTANT: Returns cached stream to prevent audio reconnection delays
   */
  getRemoteStream(callId: string): MediaStream | null {
    logger.debug(`getRemoteStream called for: ${callId}`);

    const pc = this.peerConnections.get(callId);
    if (!pc) {
      logger.error(`No peer connection found for: ${callId}`);
      return null;
    }

    logger.debug(
      `PC state: ${pc.connectionState}, ICE: ${pc.iceConnectionState}`
    );

    // Return cached stream if it exists and has tracks
    const cachedStream = this.remoteStreams.get(callId);
    if (cachedStream && cachedStream.getTracks().length > 0) {
      logger.debug(
        `Returning CACHED stream with ${cachedStream.getTracks().length} tracks`
      );
      return cachedStream;
    }

    // Create new stream only if needed
    logger.debug('Creating NEW stream from receivers');
    const remoteStream = new MediaStream();
    const receivers = pc.getReceivers();
    logger.debug(`Found ${receivers.length} receivers`);

    receivers.forEach((receiver, idx) => {
      if (receiver.track) {
        logger.debug(
          `Receiver ${idx}: ${receiver.track.kind}, enabled: ${String(
            receiver.track.enabled
          )}, state: ${receiver.track.readyState}`
        );
        remoteStream.addTrack(receiver.track);
      }
    });

    // Cache the stream
    if (remoteStream.getTracks().length > 0) {
      this.remoteStreams.set(callId, remoteStream);
      logger.debug(
        `Cached NEW remote stream for: ${callId} with ${
          remoteStream.getTracks().length
        } tracks`
      );
    } else {
      logger.warn('Created stream has NO tracks!');
    }

    return remoteStream;
  }

  /**
   * Change microphone device for active call
   * Preserves the call's current mute state on the replacement track — a
   * freshly acquired getUserMedia track defaults to enabled: true, which
   * would silently unmute a muted call otherwise.
   */
  async setMicrophone(callId: string, deviceId: string): Promise<void> {
    const pc = this.peerConnections.get(callId);
    if (!pc) {
      logger.error(`No peer connection for call: ${callId}`);
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: deviceId } },
      });

      const audioTrack = stream.getAudioTracks()[0];
      const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');

      if (sender && audioTrack) {
        const isMuted =
          useSoftphoneStore.getState().activeCalls.get(callId)?.isMuted ??
          false;
        audioTrack.enabled = !isMuted;
        await sender.replaceTrack(audioTrack);
        logger.info(`Microphone changed to: ${deviceId}`, { isMuted });
      }
    } catch (error) {
      logger.error(
        'Failed to change microphone',
        error instanceof Error ? error : { error: String(error) }
      );
    }
  }

  /**
   * Mute local audio track for a call
   * Disables the audio track without removing it
   */
  muteLocalAudio(callId: string): boolean {
    const pc = this.peerConnections.get(callId);
    if (!pc) {
      logger.error(`Cannot mute: No peer connection for call ${callId}`);
      return false;
    }

    const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
    if (sender?.track) {
      sender.track.enabled = false;
      logger.info(`Local audio muted for call: ${callId}`);
      return true;
    }

    logger.warn(`Cannot mute: No audio track found for call ${callId}`);
    return false;
  }

  /**
   * Unmute local audio track for a call
   * Re-enables the audio track
   */
  unmuteLocalAudio(callId: string): boolean {
    const pc = this.peerConnections.get(callId);
    if (!pc) {
      logger.error(`Cannot unmute: No peer connection for call ${callId}`);
      return false;
    }

    const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
    if (sender?.track) {
      sender.track.enabled = true;
      logger.info(`Local audio unmuted for call: ${callId}`);
      return true;
    }

    logger.warn(`Cannot unmute: No audio track found for call ${callId}`);
    return false;
  }

  /**
   * Check if local audio is muted
   */
  isLocalAudioMuted(callId: string): boolean {
    const pc = this.peerConnections.get(callId);
    if (!pc) return false;

    const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
    return sender?.track ? !sender.track.enabled : true;
  }

  /**
   * Clear cached stream for a call
   */
  clearCachedStream(callId: string): void {
    const stream = this.remoteStreams.get(callId);
    if (stream) {
      logger.debug(`Clearing cached stream for: ${callId}`);
      this.remoteStreams.delete(callId);
    }
  }

  /**
   * Cleanup peer connection for a specific call
   */
  cleanup(callId: string): void {
    const pc = this.peerConnections.get(callId);
    if (pc) {
      logger.debug(`Closing peer connection for: ${callId}`);
      pc.close();
      this.peerConnections.delete(callId);
    }

    const stream = this.remoteStreams.get(callId);
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      this.remoteStreams.delete(callId);
      logger.debug(`Cleaned up cached stream for: ${callId}`);
    }
  }

  /**
   * Cleanup all peer connections
   *
   * IMPORTANT: Preserves peer connections and streams for active calls to ensure
   * call audio continues working during Provider remounts.
   */
  cleanupAll(): void {
    // Get active call IDs from store to preserve their connections
    const activeCallIds = new Set(useSoftphoneStore.getState().activeCalls.keys());
    const hasActiveCalls = activeCallIds.size > 0;

    if (hasActiveCalls) {
      logger.debug('Active calls detected, preserving their peer connections', {
        activeCallIds: Array.from(activeCallIds),
      });
    }

    logger.debug('Closing peer connections (except active calls)');

    // Close peer connections for non-active calls only
    const pcsToRemove: string[] = [];
    this.peerConnections.forEach((pc, callId) => {
      if (!activeCallIds.has(callId)) {
        pc.close();
        pcsToRemove.push(callId);
      } else {
        logger.debug(`Preserving peer connection for active call: ${callId}`);
      }
    });
    pcsToRemove.forEach((callId) => this.peerConnections.delete(callId));

    // Clean up streams for non-active calls only
    const streamsToRemove: string[] = [];
    this.remoteStreams.forEach((stream, callId) => {
      if (!activeCallIds.has(callId)) {
        stream.getTracks().forEach((track) => track.stop());
        streamsToRemove.push(callId);
      } else {
        logger.debug(`Preserving remote stream for active call: ${callId}`);
      }
    });
    streamsToRemove.forEach((callId) => this.remoteStreams.delete(callId));

    logger.debug('Cleaned up cached streams (except active calls)', {
      preservedConnections: this.peerConnections.size,
      preservedStreams: this.remoteStreams.size,
    });
  }

  /**
   * Get peer connection for debugging
   */
  getPeerConnection(callId: string): RTCPeerConnection | undefined {
    return this.peerConnections.get(callId);
  }
}
